import React, { useEffect, useState } from 'react';
import { BoxArrowInUpRight } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import Loader from '../../common-components/loader';
import constants from '../../constants';
import { Spin, Table } from 'antd';
import AlgorandAccountService from '../../services/getAlgoAccount';
import moment from 'moment';
import { getDashboard } from '../../api/dashboard';
import InfoCircle from '@mui/icons-material/InfoOutlined';
import './dashboard.scss';
import { classForKey } from '../../utils/classForKey';
import ShowAccounts from '../../Components/Dashboard/ShowAccounts';
import { Button, Col, OverlayTrigger, Popover, Row } from 'react-bootstrap';
import { accountLink } from '../../utils/nodeExplorer';

interface DashboardObject {
  type: string;
  total: number;
  timeNull: number;
  time1Month: number;
  time1Year: number;
  time120: number;
  time24: number;
  time1: number;
}

const Dashboard = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(true);
  const [dashboard, setDashboard] = useState<DashboardObject>();
  const [data, setData] = useState<DashboardObject[]>([]);
  const [dataSource, setDataSource] = useState<any>();
  const [spinning, setSpinning] = useState(false);
  // const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
  //   null
  // );

  const algorandAccountService = new AlgorandAccountService();

  const getAccountData = async (request: { account: string, transaction: boolean }): Promise<unknown> => {
    const accountRes = await algorandAccountService
      ?.getAccount(request.account)
      .then(async (res: any) => {
        if (request.transaction) {
          const transactions = await algorandAccountService
            ?.getTransaction(request.account)
            .then((result: any) => {
              return result;
            });
          let mergedRes = { ...res, ...transactions };
          return mergedRes;
        }
        return res;
      })
      .catch((error) => {
        console.log('Error retrieving account or transactions', request, error);
        return null;
      });
    return accountRes;
  };

  const getData = async () => {
    try {
      const result = await getDashboard();
      if (result.data.success) {
        const dashboardData = result.data.data.reduce(
          (total: DashboardObject, currentValue: DashboardObject) => ({
            /* eslint-disable-next-line i18next/no-literal-string */
            type: 'Total',
            total: total.total + currentValue.total,
            timeNull: total.timeNull + currentValue.timeNull,
            time1Month: total.time1Month + currentValue.time1Month,
            time1Year: total.time1Year + currentValue.time1Year,
            time120: total.time120 + currentValue.time120,
            time24: total.time24 + currentValue.time24,
            time1: total.time1 + currentValue.time1
          })
        );
        setLoading(false);
        setDashboard(dashboardData);
        setData(result.data.data);
      }
    } catch (error) {
      console.log(error);
    }

    setSpinning(true);

    const accountsToRequest = [
      { account: constants?.transaction?.recicleBinType1, transaction: true },
      { account: constants?.transaction?.recicleBinType2, transaction: true },
      { account: constants?.transaction?.recicleBinType3, transaction: true },
      { account: constants?.transaction?.recicleBinType4, transaction: true },
      { account: constants?.transaction?.burning, transaction: false },
      { account: constants?.transaction?.rewarder, transaction: false }
    ];

    const promises: Promise<unknown>[] = [];
    for (const request of accountsToRequest) {
      promises.push(getAccountData(request));
    }

    const dataSourceValue = await Promise.all(promises);

    setDataSource(dataSourceValue.filter((x) => x !== null));
    setSpinning(false);
  };

  useEffect(() => {
    getData();
  }, []);

  const columns = [
    {
      title: 'Address Name',
      dataIndex: 'address',
      key: 'address',
      // eslint-disable-next-line react/display-name
      render: (text: any) => {
        let tabValue;
        switch (text) {
          case constants?.transaction?.recicleBinType1:
            tabValue = 'Recycle Bin Type 1';
            break;
          case constants?.transaction?.recicleBinType2:
            tabValue = 'Recycle Bin Type 2';
            break;
          case constants?.transaction?.recicleBinType3:
            tabValue = 'Recycle Bin Type 3';
            break;
          case constants?.transaction?.recicleBinType4:
            tabValue = 'Recycle Bin Type 4';
            break;
          case constants?.transaction?.burning:
            tabValue = 'Burning';
            break;
          case constants?.transaction?.rewarder:
            tabValue = 'Rewarder';
        }
        return (
          <p>
            {tabValue && (
              <a
                href={accountLink(text)}
                target="_blank"
              >
                {tabValue}
                <BoxArrowInUpRight></BoxArrowInUpRight>
              </a>
            )}
          </p>
        );
      }
    },
    {
      title: 'Algo Balance',
      dataIndex: 'amount',
      key: 'amount',
      // eslint-disable-next-line react/display-name
      render: (text: any) => {
        return <p>{Number(text) / 1000000} Algos</p>;
      }
    },
    {
      title: 'Planet Recicle Balance',
      dataIndex: 'assets',
      key: 'assets',
      // eslint-disable-next-line react/display-name
      render: (record: any) => {
        return <p>{Number(record[0].amount) / 1000000} PW_BIN</p>;
      }
    },
    {
      title: 'Planets',
      dataIndex: '',
      // eslint-disable-next-line react/display-name
      key: 'planets',
      render: (record: any) => {
        if (record.address === constants?.transaction?.rewarder) {
          const planet = record.assets.map((item: any) => {
            console.log(item['asset-id']);
            if (item['asset-id'] === 27165954) {
              return item?.amount;
            }
          });
          return (
            <p>
              {planet.map((item: any) => {
                return item && `${Number(item) / 1000000} PW`;
              })}
            </p>
          );
        } else {
          return '-';
        }
      }
    },
    {
      title: 'Last Transaction Date',
      dataIndex: 'round-time',
      key: 'round-time',
      // eslint-disable-next-line react/display-name
      render: (text: any) => {
        return (
          <p>
            {moment(Number(text) * 1000).format('DD/MM/YYYY') === 'Invalid date'
              ? '-'
              : moment(Number(text) * 1000).format('DD/MM/YYYY')}
          </p>
        );
      }
    },
    {
      title: 'Amount Last Transaction',
      dataIndex: '',
      key: 'amount',
      render: (record: any) => {
        let algo = false;
        let planet = false;
        let sender = false;
        if (record.assets[0]['asset-id'] === 395684495) {
          algo = true;
        } else {
          if (record.assets[0]['asset-id'] === 27165954) planet = true;
        }
        if (
          record.address !== constants?.transaction?.burning &&
          record.address !== constants?.transaction?.rewarder
        ) {
          if (record.sender === record.address) {
            sender = true;
          }
          return (
            <p
              style={{
                color: sender ? 'red' : 'green',
                fontWeight: 'bold'
              }}
            >
              {sender
                ? `- ${Number(record['asset-transfer-transaction'].amount) /
                1000000
                }`
                : `+ ${Number(record['asset-transfer-transaction'].amount) /
                1000000
                }`}
              {algo && ' Algos'} {planet && ' PW_BIN'}
            </p>
          );
        } else {
          return (
            <p
              style={{
                fontWeight: 'bold'
              }}
            >
              -
            </p>
          );
        }
      }
    }
  ];

  const tableRows = [
    { title: t('NEVER_TRANSMITTED'), key: 'timeNull' },
    { title: t('1_YEAR'), key: 'time1Year' },
    { title: t('1_MONTH'), key: 'time1Month' },
    { title: t('5_DAYS'), key: 'time120' },
    { title: t('1_DAY'), key: 'time24' },
    { title: t('1_HOUR'), key: 'time1' }
  ];


  const showDetails = (props: any, key: string) => {
    const className = classForKey(key);
    return (
      <Popover id='popover-basic' {...props} title={t('DETAIL_BY_TYPE')}>

        {data!.map((x: any) => (
          <div key={key + x.code} className='progress-group'>
            <span className='progress-text'>{x.code}</span>
            <span className='float-right text-sm'><b>{x[key]}</b>/{(dashboard as any)[key]}</span>

            <div className='progress sm'>
              <div className={`progress-bar ${className}`}
                style={{ width: `${(100 * (x[key] ?? 0)) / ((dashboard as any)[key] ?? 1)}%` }}>
              </div>
            </div>
          </div>
        ))}
      </Popover>
    );
  };




  return (
    <>
      <Row>
        <Col md='6'>
          <div className='card card-dark'>
            <div className='card-header'>{t('NO_TRANSMISSION_SENSORS')}</div>
            <div className='card-body'>
              {tableRows.map((x: any) => (
                <Row key={`key-${x.key}`}>
                  <Col xs={11}>
                    <div className='progress-group'>
                      <span className='progress-text'>{x.title}</span>
                      <span className='float-right text-sm'>
                        <b>{(dashboard as any)?.[x.key]}</b>/{dashboard?.total}
                      </span>

                      <div className='progress sm'>
                        <div className={`progress-bar ${classForKey(x.key)}`}
                          style={
                            { width: `${(100 * ((dashboard as any)?.[x.key] ?? 0)) / (dashboard?.total ?? 1)}%` }
                          }>
                        </div>
                      </div>
                    </div>
                  </Col>
                  <Col xs={1} className='align-self-center'>
                    <OverlayTrigger placement='right' overlay={(props: any) => showDetails(props, x.key)}>
                      <Button variant='link'><InfoCircle /></Button>
                    </OverlayTrigger>
                  </Col>
                </Row>
              ))}
            </div>
            {loading &&
              <div className='overlay'>
                <Loader visible={loading} />
              </div>}
          </div>
        </Col>
        <ShowAccounts loading={loading}></ShowAccounts>
      </Row>

      <Table
        loading={{
          indicator: (
            <div>
              <Spin />
            </div>
          ),
          spinning: spinning
        }}
        dataSource={dataSource}
        columns={columns}
        style={{
          marginTop: 20
        }}
      />
    </>
  );
};


export default Dashboard;
